import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import MethodBadge from './MethodBadge';
import RecentSection from './RecentSection';
import type { Method } from '@/lib/tokens';

export interface Endpoint {
    operationId: string;
    method: Method;
    path: string;
    summary: string;
    tag: string;
}

interface Props {
    endpoints: Endpoint[];
    isOpen?: boolean;
    onClose?: () => void;
}

export default function Sidebar({ endpoints, isOpen = false, onClose }: Props) {
    const [q, setQ] = useState('');

    const grouped = useMemo(() => {
        const filtered = q.trim()
            ? endpoints.filter((e) =>
                e.summary.toLowerCase().includes(q.toLowerCase())
                || e.path.toLowerCase().includes(q.toLowerCase()))
            : endpoints;
        const map = new Map<string, Endpoint[]>();
        for (const e of filtered) {
            if (!map.has(e.tag)) map.set(e.tag, []);
            map.get(e.tag)!.push(e);
        }
        return Array.from(map.entries());
    }, [endpoints, q]);

    // Lock body scroll while mobile drawer is open.
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);

    return (
        <>
            {/* Backdrop — mobile only, when drawer is open. */}
            {isOpen && (
                <div
                    onClick={onClose}
                    className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
                    aria-hidden="true"
                />
            )}

            <aside
                className={`
                    fixed md:sticky top-14 left-0 z-40 md:z-auto
                    w-[272px] shrink-0 h-[calc(100vh-56px)]
                    overflow-y-auto transform transition-transform duration-200
                    ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}
                style={{ borderRight: '1px solid var(--border)', backgroundColor: 'var(--surface-1)' }}
                aria-hidden={!isOpen}
            >
                <div className="px-4 py-4 flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'var(--ghost)' }} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cari endpoint…"
                            className="w-full h-8 pl-8 pr-2 rounded-md text-sm outline-none transition-colors"
                            style={{
                                backgroundColor: 'var(--bg)',
                                border: '1px solid var(--border)',
                                color: 'var(--ink)',
                            }}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = 'var(--accent)';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = 'var(--border)';
                            }}
                        />
                    </div>
                    {/* Close button — mobile only */}
                    <button
                        onClick={onClose}
                        className="md:hidden p-1.5 rounded-md text-muted hover:text-ink hover:bg-surface-2 transition-colors"
                        aria-label="Tutup menu"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <nav className="px-3 pb-12 space-y-5">
                    <RecentSection />
                    {grouped.length === 0 && (
                        <p className="px-2 text-xs" style={{ color: 'var(--ghost)' }}>Tidak ada endpoint.</p>
                    )}
                    {grouped.map(([tag, items]) => (
                        <div key={tag}>
                            <h3 className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{
                                color: 'var(--ghost)',
                                letterSpacing: '0.05em',
                            }}>
                                {tag}
                            </h3>
                            <ul className="space-y-0.5">
                                {items.map((e) => (
                                    <li key={e.operationId}>
                                        <NavLink
                                            to={`/r/${e.operationId}`}
                                            onClick={onClose}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] transition-colors"
                                            style={({ isActive }) => ({
                                                color: isActive ? 'var(--ink)' : 'var(--muted)',
                                                backgroundColor: isActive ? 'var(--surface-active)' : 'transparent',
                                                fontWeight: isActive ? 600 : 400,
                                                textDecoration: 'none',
                                                borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                                                paddingLeft: isActive ? '6px' : '8px',
                                            })}
                                            onMouseEnter={(ev) => {
                                                const el = ev.currentTarget;
                                                if (el.style.backgroundColor !== 'var(--surface-active)' && !el.style.backgroundColor.includes('eef2ff')) {
                                                    el.style.backgroundColor = 'var(--surface-2)';
                                                    el.style.color = 'var(--ink)';
                                                }
                                            }}
                                            onMouseLeave={(ev) => {
                                                const el = ev.currentTarget;
                                                if (!el.style.backgroundColor.includes('eef2ff')) {
                                                    el.style.backgroundColor = 'transparent';
                                                    el.style.color = 'var(--muted)';
                                                }
                                            }}
                                        >
                                            <MethodBadge method={e.method} size="xs" />
                                            <span className="truncate flex-1">{e.summary}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>
            </aside>
        </>
    );
}
